import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { InputNumber, Modal, Slider, Checkbox, Tooltip, Input } from "antd";
import { faEye, faEyeSlash, faChevronLeft, faChevronRight, faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import {
    setClassificationVisibility,
    setClassificationScoreFilter,
    setCurrentInspectionMarkup,
    reviewClassificationObservations,
    setClassificationsTabOpen,
} from "redux/actions";
import { binarySearch } from "../../../util/PlaylistUtils";
import _ from "lodash";
import OBCSpinner from "../../../util/OBC";
import Tippy from "@tippyjs/react";

const classificationsVisibleSelector = (state) => state.railInspection.classifications.visibility;
const observationDataSelector = (state) => state.railInspection.classifications.data;
const selectedRailInspectionImageSelector = (state) => state.railInspection.selectedRailInspectionImage;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const currentClassificationIDSelector = (state) => state.railInspection.classifications.selectedClassification;
const railConfigSelector = (state) => state.railInspection.railInspectionImageConfig;
const allClassificationsFetchedSelector = (state) => state.railInspection.classifications.allClassificationsFetched;
const scoreLimitSelector = (state) => state.railInspection.classifications.score;
const leftRightConfigSelector = (state) => state.railInspection.selectedRailInspectionImage.leftRightConfig;
const superUserSelector = (state) => state.userDetails.userConfig.super_admin;
const sessionIDSelector = (state) => state.playlist.data.routeID;

const SelectableInput = (props) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current.input) {
            inputRef.current.input.select();
        }
    }, []);

    return (
        <Input
            ref={inputRef}
            {...props}
        />
    );
};

const ClassificationsNavigation = ({ shortcutsActive }) => {
    const dispatch = useDispatch();

    const classificationsVisible = useSelector(classificationsVisibleSelector);
    const observationData = useSelector(observationDataSelector);
    const selectedRailInspectionImage = useSelector(selectedRailInspectionImageSelector);
    const railData = useSelector(railDataObjectSelector);
    const currentClassificationID = useSelector(currentClassificationIDSelector);
    const railConfig = useSelector(railConfigSelector);
    const allClassificationsFetched = useSelector(allClassificationsFetchedSelector);
    const scoreLimit = useSelector(scoreLimitSelector);
    const leftRightConfig = useSelector(leftRightConfigSelector);
    const superAdmin = useSelector(superUserSelector);
    const sessionID = useSelector(sessionIDSelector);

    const [displayFilters, setDisplayFilters] = useState(false);
    const [autoNavigation, setAutoNavigation] = useState(true);
    const [verifiedFilter, setVerifiedFilter] = useState(true);
    const [rejectedFilter, setRejectedFilter] = useState(true);
    const [reviewNeededFilter, setReviewNeededFilter] = useState(true);
    const [updatingPosition, setUpdatingPosition] = useState(false);
    const [sliderScoreLimit, setSliderScoreLimit] = useState(scoreLimit);

    useEffect(() => {
        dispatch(setClassificationsTabOpen(true));

        return () => {
            dispatch(setClassificationScoreFilter(0));
        };
    }, [dispatch]);

    const filteredObservationData = useMemo(() => {
        return observationData.filter((item) => {
            const highestScoreClassification = _.maxBy(item.classifications, "score");
            return (
                ((verifiedFilter && item.review_status === 1) ||
                    (rejectedFilter && item.review_status === 3) ||
                    (reviewNeededFilter && item.review_status === 0)) &&
                highestScoreClassification.score >= scoreLimit
            );
        });
    }, [observationData, rejectedFilter, reviewNeededFilter, scoreLimit, verifiedFilter]);

    const classificationsList = useMemo(() => {
        return _.flatMap(filteredObservationData, "classifications");
    }, [filteredObservationData]);

    const currentImageHasClassification = useMemo(() => {
        if (classificationsList && classificationsList.length && selectedRailInspectionImage && selectedRailInspectionImage.timestamp) {
            return _.some(classificationsList, (classification) => classification.image_timestamp === selectedRailInspectionImage.timestamp);
        }
        return false;
    }, [classificationsList, selectedRailInspectionImage]);

    const currentClassification = useMemo(() => {
        return _.find(classificationsList, { id: currentClassificationID }) ?? null;
    }, [currentClassificationID, classificationsList]);

    const currentObservation = useMemo(() => {
        if (currentClassification && currentClassification.id) {
            return (
                _.find(filteredObservationData, (observation) => {
                    return _.some(observation.classifications, (classification) => classification.id === currentClassification.id);
                }) ?? null
            );
        }
        return null;
    }, [filteredObservationData, currentClassification]);

    const closestToLeftClassificationIndex = useMemo(() => {
        // If current image has classification this will return the index of that classification (not the one to the left)
        if (!selectedRailInspectionImage || !classificationsList || !classificationsList.length) {
            return null;
        }
        return binarySearch(selectedRailInspectionImage.timestamp, classificationsList, (c) => c.image_timestamp);
    }, [selectedRailInspectionImage, classificationsList]);

    const closestToRightClassificationIndex = useMemo(() => {
        if (!selectedRailInspectionImage || !classificationsList || !classificationsList.length) {
            return null;
        }
        if (
            classificationsList[closestToLeftClassificationIndex].image_timestamp > selectedRailInspectionImage.timestamp ||
            closestToLeftClassificationIndex >= classificationsList.length - 1
        ) {
            return closestToLeftClassificationIndex;
        } else {
            return closestToLeftClassificationIndex + 1;
        }
    }, [classificationsList, closestToLeftClassificationIndex, selectedRailInspectionImage]);

    const closestToLeftObservationIndex = useMemo(() => {
        if (
            !selectedRailInspectionImage ||
            !filteredObservationData ||
            !filteredObservationData.length ||
            !classificationsList ||
            !classificationsList.length ||
            _.isNil(closestToLeftClassificationIndex)
        ) {
            return null;
        }

        return _.findIndex(filteredObservationData, (obs) =>
            _.some(obs.classifications, (classification) => classification.id === classificationsList[closestToLeftClassificationIndex].id),
        );
    }, [selectedRailInspectionImage, filteredObservationData, classificationsList, closestToLeftClassificationIndex]);

    const closestToRightObservationIndex = useMemo(() => {
        if (
            !selectedRailInspectionImage ||
            !filteredObservationData ||
            !filteredObservationData.length ||
            !classificationsList ||
            !classificationsList.length ||
            _.isNil(closestToLeftObservationIndex)
        ) {
            return null;
        }
        if (
            (closestToLeftObservationIndex === 0 &&
                selectedRailInspectionImage.timestamp < _.min(_.map(filteredObservationData[0].classifications, (c) => c.image_timestamp))) ||
            closestToLeftObservationIndex >= filteredObservationData.length - 1
        ) {
            return closestToLeftObservationIndex;
        }

        return closestToLeftObservationIndex + 1;
    }, [classificationsList, closestToLeftObservationIndex, selectedRailInspectionImage, filteredObservationData]);

    const distanceToRight = useMemo(() => {
        if (
            filteredObservationData &&
            filteredObservationData.length &&
            railData &&
            !_.isNil(closestToRightObservationIndex) &&
            (closestToRightObservationIndex !== closestToLeftObservationIndex || closestToRightObservationIndex === 0) &&
            selectedRailInspectionImage &&
            selectedRailInspectionImage.timestamp
        ) {
            const closestToRight = filteredObservationData[closestToRightObservationIndex];
            const rightIndex = _.findIndex(
                railData,
                (i) => i.timestamp === _.minBy(_.get(closestToRight, "classifications", []), "image_timestamp").image_timestamp,
            );
            const currentIndex = _.findIndex(railData, (i) => i.timestamp === selectedRailInspectionImage.timestamp);

            if (rightIndex > -1 && currentIndex > -1) {
                return rightIndex - currentIndex;
            }
        }
        return null;
    }, [filteredObservationData, railData, selectedRailInspectionImage, closestToRightObservationIndex, closestToLeftObservationIndex]);

    const distanceToLeft = useMemo(() => {
        if (
            filteredObservationData &&
            filteredObservationData.length &&
            railData &&
            !_.isNil(closestToLeftObservationIndex) &&
            (closestToLeftObservationIndex !== closestToRightObservationIndex || closestToLeftObservationIndex === filteredObservationData.length - 1) &&
            selectedRailInspectionImage &&
            selectedRailInspectionImage.timestamp
        ) {
            let offset = 0;
            if (currentImageHasClassification && closestToLeftObservationIndex > 0) {
                offset = 1;
            }
            const closestToLeft = filteredObservationData[closestToLeftObservationIndex - offset];
            const leftIndex = _.findIndex(
                railData,
                (i) => i.timestamp === _.maxBy(_.get(closestToLeft, "classifications", []), "image_timestamp").image_timestamp,
            );
            const currentIndex = _.findIndex(railData, (i) => i.timestamp === selectedRailInspectionImage.timestamp);

            if (leftIndex > -1 && currentIndex > -1) {
                return currentIndex - leftIndex >= 0 ? currentIndex - leftIndex : 0;
            }
        }
        return null;
    }, [
        closestToLeftObservationIndex,
        closestToRightObservationIndex,
        filteredObservationData,
        railData,
        selectedRailInspectionImage,
        currentImageHasClassification,
    ]);

    const currentObservationIndex = useMemo(() => {
        if (currentObservation) {
            return _.findIndex(filteredObservationData, { observation_number: currentObservation.observation_number });
        }
        return null;
    }, [filteredObservationData, currentObservation]);

    const closestIndex = useMemo(() => {
        if (railData && selectedRailInspectionImage) {
            if (currentObservationIndex >= 0 && !_.isNil(currentObservationIndex)) {
                return currentObservationIndex;
            }
            if (!_.isNil(distanceToLeft) || !_.isNil(distanceToRight)) {
                if (_.isNil(distanceToLeft)) {
                    return closestToRightObservationIndex;
                } else if (_.isNil(distanceToRight)) {
                    return closestToLeftObservationIndex;
                } else if (distanceToLeft < distanceToRight) {
                    return closestToLeftObservationIndex;
                } else {
                    return closestToRightObservationIndex;
                }
            }
        }
        return null;
    }, [
        currentObservationIndex,
        railData,
        selectedRailInspectionImage,
        distanceToLeft,
        distanceToRight,
        closestToRightObservationIndex,
        closestToLeftObservationIndex,
    ]);

    const navigate = useCallback(
        (index) => {
            if (
                filteredObservationData &&
                filteredObservationData.length &&
                filteredObservationData[index] &&
                filteredObservationData[index]?.classifications?.length
            ) {
                const lowestTimestampClassification = _.minBy(filteredObservationData[index].classifications, "image_timestamp");
                dispatch(setCurrentInspectionMarkup("classification", lowestTimestampClassification.id));
            }
        },
        [dispatch, filteredObservationData],
    );

    const disableBack = useMemo(() => {
        return (
            !filteredObservationData.length ||
            _.isNil(closestToLeftObservationIndex) ||
            currentObservationIndex === 0 ||
            (closestToLeftObservationIndex === closestToRightObservationIndex && closestToLeftObservationIndex === 0)
        );
    }, [closestToLeftObservationIndex, closestToRightObservationIndex, filteredObservationData.length, currentObservationIndex]);

    const disableForward = useMemo(() => {
        return (
            !filteredObservationData.length ||
            _.isNil(closestToRightObservationIndex) ||
            currentObservationIndex === filteredObservationData.length - 1 ||
            (closestToLeftObservationIndex === closestToRightObservationIndex && closestToRightObservationIndex === filteredObservationData.length - 1)
        );
    }, [closestToRightObservationIndex, closestToLeftObservationIndex, currentObservationIndex, filteredObservationData.length]);

    const goForward = useCallback(() => {
        if (!disableForward && closestToRightObservationIndex > -1) {
            navigate(closestToRightObservationIndex);
        }
    }, [navigate, closestToRightObservationIndex, disableForward]);

    const goBack = useCallback(() => {
        if (!disableBack && closestToLeftObservationIndex > -1) {
            if (currentObservationIndex && currentObservationIndex > 0) {
                navigate(currentObservationIndex - 1);
            } else {
                navigate(closestToLeftObservationIndex);
            }
        }
    }, [navigate, currentObservationIndex, closestToLeftObservationIndex, disableBack]);

    const revMarker = useCallback(
        (review) => {
            dispatch(reviewClassificationObservations(sessionID, currentObservation.observation_number, review));
            if (autoNavigation) {
                goForward();
            }
        },
        [currentObservation, dispatch, goForward, autoNavigation, sessionID],
    );

    const handleKeyPress = useCallback(
        (event) => {
            if (!shortcutsActive) {
                return;
            }
            if (event.key === ",") {
                goBack();
            } else if (event.key === ".") {
                goForward();
            } else if (event.key === "v" || event.key === "V") {
                revMarker(1);
            } else if (event.key === "r" || event.key === "R") {
                revMarker(3);
            }
        },
        [goBack, goForward, shortcutsActive, revMarker],
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyPress, false);

        return () => {
            document.removeEventListener("keydown", handleKeyPress, false);
        };
    }, [handleKeyPress]);

    const distanceFromClassification = useMemo(() => {
        let position;
        let directionIcon;

        if (!distanceToLeft && !distanceToRight) {
            position = 0;
            directionIcon = null;
        } else {
            if (!distanceToLeft || (distanceToLeft > distanceToRight && !_.isNil(distanceToRight))) {
                position = distanceToRight;
                directionIcon = <FontAwesomeIcon icon={faArrowRight} />;
            } else {
                position = distanceToLeft;
                directionIcon = <FontAwesomeIcon icon={faArrowLeft} />;
            }
        }

        return (
            <div
                style={{ color: "#a9c447", fontWeight: "bold" }}
                className="distance">
                <span>{Math.abs(position)}m </span>
                {directionIcon}
            </div>
        );
    }, [distanceToLeft, distanceToRight]);

    const railScores = useMemo(() => {
        let railScoresStr = "";

        if (currentClassification && leftRightConfig) {
            if (currentClassification.bbox.left_rail.class === "contaminated") {
                railScoresStr += `${leftRightConfig.top === "left" ? "Left rail" : "Right rail"} (${Math.round(currentClassification.bbox.left_rail.score * 100)}%)`;
            }
            if (currentClassification.bbox.right_rail.class === "contaminated") {
                if (railScoresStr.length > 0) {
                    railScoresStr += " - ";
                }
                railScoresStr += `${leftRightConfig.top === "left" ? "Right rail" : "Left rail"} (${Math.round(currentClassification.bbox.right_rail.score * 100)}%)`;
            }
        }

        return railScoresStr;
    }, [currentClassification, leftRightConfig]);

    const classificationItem = useMemo(() => {
        if (railConfig && railConfig.inspection_images && railConfig.inspection_images.length) {
            return (
                <div className="InspectRailClassificationsMain">
                    <div className={`InspectRailClassificationsMainImage ${Math.abs(distanceToLeft) === 0 ? "current" : ""}`}>
                        {currentClassification && currentImageHasClassification && superAdmin && (
                            <Tippy
                                content={currentClassification.image_timestamp}
                                placement="bottom"
                                arrow={true}>
                                <div className="InspectionRailClassificationsTooltip" />
                            </Tippy>
                        )}
                    </div>
                    <div className="InspectRailClassificationsMainInfoContent">
                        {currentClassification && currentImageHasClassification ? (
                            <>
                                <div className="InspectRailClassificationsMainInfo">
                                    <h2 className="InspectRailClassificationsMainTitle">{currentClassification.classifier_type}</h2>
                                    <p className="InspectRailClassificationsMainValue">{currentClassification.id}</p>
                                </div>
                                <div className="InspectRailClassificationsMainInfo">
                                    <div className="InspectRailClassificationsMainTitle">{currentClassification.result}</div>
                                </div>
                                <div className="InspectRailClassificationsMainInfo">
                                    <div className="InspectRailClassificationsMainTitle">Score:</div>
                                    <div className="InspectRailClassificationsMainValue Success">{currentClassification.score}%</div>
                                </div>
                                <div className="InspectRailClassificationsMainInfo">
                                    <div className="InspectRailClassificationsMainValue Success">{railScores}</div>
                                </div>
                                {distanceFromClassification}
                            </>
                        ) : (
                            <div className="InspectRailClassificationsMainInfo">
                                <h2 className="InspectRailClassificationsMainTitle">No result</h2>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
    }, [currentClassification, railConfig, distanceFromClassification, distanceToLeft, railScores, currentImageHasClassification, superAdmin]);

    const setCurrentIndex = useCallback(
        (e) => {
            let index = Number(e.target.value);

            if (index < 1) {
                index = 1;
            } else if (index >= filteredObservationData.length) {
                index = filteredObservationData.length;
            }

            navigate(index - 1);
            setUpdatingPosition(false);
        },
        [filteredObservationData, navigate],
    );

    const classificationIndex = useMemo(() => {
        if (closestIndex > -1 && filteredObservationData && filteredObservationData.length) {
            return (
                <div
                    className="markerIndex"
                    onDoubleClick={() => {
                        setUpdatingPosition(true);
                    }}>
                    {updatingPosition ? (
                        <span>
                            <SelectableInput
                                type="number"
                                size="small"
                                autoFocus={true}
                                style={{ width: "50px", textAlign: "right" }}
                                defaultValue={closestIndex + 1}
                                onBlur={setCurrentIndex}
                                onPressEnter={setCurrentIndex}
                            />
                            <span>of</span>
                            <p>{filteredObservationData.length}</p>
                        </span>
                    ) : (
                        <Tooltip title="Double click to navigate to a specific classification">
                            <p>{closestIndex + 1}</p>
                            <span>of</span>
                            <p>{filteredObservationData.length}</p>
                        </Tooltip>
                    )}
                </div>
            );
        } else {
            return null;
        }
    }, [filteredObservationData, closestIndex, updatingPosition, setCurrentIndex]);

    const navigationButtons = useMemo(() => {
        return (
            <div className="markerControls wide">
                <div
                    className={`markerControls__Item ${disableBack ? "disabled" : ""}`}
                    onClick={goBack}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    <span>Back (,)</span>
                </div>
                {classificationIndex}
                <div
                    className={`markerControls__Item ${disableForward ? "disabled" : ""}`}
                    onClick={goForward}>
                    <span>Next (.)</span>
                    <FontAwesomeIcon icon={faChevronRight} />
                </div>
            </div>
        );
    }, [classificationIndex, goBack, goForward, disableForward, disableBack]);

    const distanceToNextClassification = useMemo(() => {
        if (selectedRailInspectionImage) {
            const index = _.findIndex(railData, (i) => i.timestamp === selectedRailInspectionImage.timestamp);
            let leftOffset = 0;
            if (currentImageHasClassification && closestToLeftClassificationIndex > 0) {
                leftOffset = 1;
            }
            let prevClassification = null;
            if (
                !_.isNil(closestToLeftClassificationIndex) &&
                (!currentImageHasClassification || closestToLeftClassificationIndex !== 0) &&
                (closestToLeftClassificationIndex !== closestToRightClassificationIndex || closestToLeftClassificationIndex !== 0)
            ) {
                prevClassification = classificationsList[closestToLeftClassificationIndex - leftOffset];
            }

            let nextClassification = null;

            if (!_.isNil(closestToRightClassificationIndex)) {
                nextClassification = classificationsList[closestToRightClassificationIndex];
            }

            let prevDistance = 0;
            let nextDistance = 0;

            if (prevClassification) {
                const prevClassificationIndex = _.findIndex(railData, (i) => i.timestamp === prevClassification.image_timestamp);
                prevDistance = index - prevClassificationIndex;
            }
            if (nextClassification) {
                const nextClassificationIndex = _.findIndex(railData, (i) => i.timestamp === nextClassification.image_timestamp);
                nextDistance = nextClassificationIndex - index;
            }

            if (prevClassification || nextClassification) {
                return (
                    <div className="InspectRailClassificationsInfo">
                        <div className="InspectRailClassificationsInfoPrev">
                            {!!prevDistance && prevDistance > 0 && (
                                <>
                                    <p>Previous Classifications</p>
                                    <span style={{ color: "#a9c447" }}>{prevDistance}m</span>
                                </>
                            )}
                        </div>
                        <div className="InspectRailClassificationsInfoNext">
                            {!!nextDistance && nextDistance > 0 && (
                                <>
                                    <p>Next Classifications</p>
                                    <span style={{ color: "#a9c447" }}>{nextDistance}m</span>
                                </>
                            )}
                        </div>
                    </div>
                );
            }
        }
    }, [
        classificationsList,
        railData,
        selectedRailInspectionImage,
        closestToLeftClassificationIndex,
        closestToRightClassificationIndex,
        currentImageHasClassification,
    ]);

    const visibilityButton = useMemo(() => {
        let icon = faEye;
        if (classificationsVisible) {
            icon = faEyeSlash;
        }

        return (
            <div
                className="inspection-default-button VisibilityButton"
                onClick={() => dispatch(setClassificationVisibility())}>
                <FontAwesomeIcon icon={icon} />
            </div>
        );
    }, [classificationsVisible, dispatch]);

    const reviewSection = useMemo(() => {
        if (currentObservation && currentImageHasClassification) {
            return (
                <div className="inspectRail__Detections__Review">
                    <div className="inspectRail__Detections__Review__Inner">
                        <button
                            className="reviewButton reject"
                            onClick={() => revMarker(3)}>
                            <span>Reject (R)</span>
                        </button>
                        <button
                            className="reviewButton verify"
                            onClick={() => revMarker(1)}>
                            <span>Verify (V)</span>
                        </button>
                    </div>
                </div>
            );
        }
    }, [currentObservation, revMarker, currentImageHasClassification]);

    let content = (
        <div className="SchemaRuns__Spinner">
            <OBCSpinner
                size={70}
                speed={3}
                colorScheme="mono"
            />
        </div>
    );

    if (allClassificationsFetched) {
        content = (
            <div className="InspectRailClassificationsContent">
                <div className="InspectRailClassificationsTop">
                    <div className="Title">Classifications</div>
                </div>
                {classificationItem}
                {distanceToNextClassification}
                {navigationButtons}
                {reviewSection}
                <div className="InspectionClassificationsRow">
                    <Tooltip title="Automatically go to next classification after selecting condition">
                        <Checkbox
                            style={{ color: "white" }}
                            checked={autoNavigation}
                            onChange={(e) => setAutoNavigation(e.target.checked)}>
                            Auto next
                        </Checkbox>
                    </Tooltip>
                    {/* -------------------------------------------------- LOOK AT THIS -------------------------------------------------- */}
                    {currentObservation && (
                        <p className="InspectionClassificationCondition">
                            {currentObservation.review_status === 1 ? "Verified" : currentObservation.review_status === 3 ? "Rejected" : ""}
                        </p>
                    )}
                    {/* -------------------------------------------------- LOOK AT THIS -------------------------------------------------- */}
                </div>
            </div>
        );
    }

    if (allClassificationsFetched && !filteredObservationData.length) {
        content = (
            <div className="InspectRailClassificationsMessage">
                <span>No Classifications</span>
            </div>
        );
    }

    return (
        <div className="InspectRailClassifications">
            <div className="Controlls">
                <button
                    className="inspection-default-button"
                    onClick={() => setDisplayFilters(true)}>
                    Filter
                </button>
                {visibilityButton}
            </div>
            <Modal
                className="InspectRailClassificationsFilters"
                title="Classification Filters"
                visible={displayFilters}
                okText="Close"
                onOk={() => setDisplayFilters(false)}
                onCancel={() => setDisplayFilters(false)}
                cancelButtonProps={{ style: { display: "none" } }}>
                <div className="InspectRailClassificationsReviewRow">
                    <h3>Review Status</h3>
                    <div className="InspectRailClassificationsReviewFilter">
                        <Checkbox
                            className="InspectRailClassificationsReviewFilter-Checkbox"
                            key={1}
                            checked={verifiedFilter}
                            onChange={(e) => setVerifiedFilter(e.target.checked)}>
                            Verified
                        </Checkbox>
                        <Checkbox
                            className="InspectRailClassificationsReviewFilter-Checkbox"
                            key={0}
                            checked={reviewNeededFilter}
                            onChange={(e) => setReviewNeededFilter(e.target.checked)}>
                            Needs Review
                        </Checkbox>
                        <Checkbox
                            className="InspectRailClassificationsReviewFilter-Checkbox"
                            key={3}
                            checked={rejectedFilter}
                            onChange={(e) => setRejectedFilter(e.target.checked)}>
                            Rejected
                        </Checkbox>
                    </div>
                </div>
                <div className="InspectRailClassificationsReviewRow">
                    <h3 className="InspectRailClassificationsScore">Score:</h3>
                    <div className="InspectRailClassificationsFiltersContainer">
                        <Slider
                            className="InspectRailClassificationsFiltersSlider"
                            min={0}
                            max={100}
                            onChange={setSliderScoreLimit}
                            onAfterChange={(value) => dispatch(setClassificationScoreFilter(value))}
                            value={sliderScoreLimit}
                        />
                        <InputNumber
                            min={0}
                            max={100}
                            value={sliderScoreLimit}
                            onChange={(value) => {
                                setSliderScoreLimit(value);
                                dispatch(setClassificationScoreFilter(value));
                            }}
                        />
                    </div>
                </div>
            </Modal>
            {content}
        </div>
    );
};

export default ClassificationsNavigation;
